import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import QhsTable from '../../../../Shared/Table/Table';
import { convertDataFromApi, useGenerateColumns } from '../../../../Shared/Table/helpers';
import { navigateToRoute, setAlertsPreFilter, TABLE_KEYS } from '../../../../../redux_store';
import useFetchList from '../../../../../hooks/useFetchList';
import { NULL_QUERY_DELAY } from '../../../../Shared/constants';
import { ALL_ROUTES } from '../../../../../config/routes';
import { camelCase } from 'lodash';
import { ASSESSMENT_STATUSES } from '../../../../Assessments/constants';
import RowActions from '../../../../Alerts/List/widgets/RowActions';
import i18next from 'i18next';
import FILTER_TYPES from '../../../../Shared/Table/widgets/Filters/constants';
import permissions from '../../../../../config/permissions';
import TableTitle from '../../../../Shared/Table/widgets/TableTitle';

export const GENDERS = {
  male: 'male',
  female: 'female',
};

export const GENDER_OPTIONS = [
  {
    label: i18next.t('tables:female'),
    value: GENDERS.female,
  },
  {
    label: i18next.t('tables:male'),
    value: GENDERS.male,
  },
];

export const GET_POPULATION_HEALTH_PATIENT_COLUMNS = [
  {
    size: 240,
    header: i18next.t('tables:patientFirstName'),
    accessorKey: 'patientFirstName',
    filter: {},
    cell: {},
  },
  {
    size: 240,
    header: i18next.t('tables:patientLastName'),
    accessorKey: 'patientLastName',
    filter: {},
    cell: {},
  },
  {
    size: 200,
    header: i18next.t('tables:patientDOB'),
    accessorKey: 'patientDateOfBirth',
    filter: {
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:mrnNumber'),
    accessorKey: 'patientMrnNumber',
    filter: {},
    cell: {},
  },
  {
    size: 230,
    header: i18next.t('tables:assessmentName'),
    accessorKey: 'entityName',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
  },
];
export const GET_DS_POPULATION_HEALTH_PATIENT_COLUMNS = [
  {
    size: 220,
    header: i18next.t('tables:account'),
    accessorKey: 'accountBusinessName',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    isDefaultCell: true,
  },
  ...GET_POPULATION_HEALTH_PATIENT_COLUMNS,
];
const PatientQhsTable = ({ metric }) => {
  const {
    metadata: { currentAccount },
    isDistributionSponsor,
  } = useSelector(state => state.user);
  const { dateRange, selectedInstrument, selectedAccount, assessmentInstruments } = useSelector(
    state => state.populationHealth
  );

  const dispatch = useDispatch();
  const {
    en: { titles },
  } = useSelector(state => state.i18n);
  const tableKey = TABLE_KEYS.patientsPopulationHealthTable;
  const _columns = !selectedAccount?.uuid
    ? GET_DS_POPULATION_HEALTH_PATIENT_COLUMNS
    : GET_POPULATION_HEALTH_PATIENT_COLUMNS;
  const fields = _columns.map(columnData => columnData.accessorKey).sort().join();
  const [modifiedAlerts, setModifiedAlerts] = useState();

  const selectedInstrumentName = useMemo(() => {
    return assessmentInstruments
      .filter(instrument => instrument.id !== 'all')
      .find(instrument => instrument.id.toLowerCase() === selectedInstrument.toLowerCase())?.name;
  }, [assessmentInstruments, selectedInstrument]);

  const baseFilter = useMemo(() => {
    const filters = [{ id: 'alertTags', value: [metric.label] }];
    if (dateRange && dateRange.startDate && dateRange.endDate) {
      filters.push({
        id: 'createdTimestamp',
        value: [dateRange.startDate, dateRange.endDate]
          .map(date => date.toLocaleDateString('en-US'))
          .join(','),
      });
    }

    if (selectedInstrumentName) {
      filters.push({
        id: 'entityName',
        value: [selectedInstrumentName],
      });
    }

    return filters;
  }, [assessmentInstruments, selectedInstrument, selectedInstrumentName]);

  const {
    items,
    totalCount,
    filter,
    sort,
    onSort,
    onFilter,
    onLoadMoreRows,
    reloadData,
    clearFilter,
    loading: dataLoading,
    additionalLoadsResults,
    fetchError,
  } = useFetchList({
    baseEndpoint: `/accounts/${selectedAccount?.uuid ?? currentAccount.uuid}/alerts`,
    baseParams: {
      fields: fields,
    },
    additionalLoads: {
      alertTags: '/accounts/alerts/tags',
    },
    baseFilter,
    tableKey,
    ignorePrefilters: true,
    canLoad: fields.length && baseFilter,
  });

  const columns = useGenerateColumns(_columns, tableKey, {}, additionalLoadsResults).columns.filter(
    column => {
      if (selectedInstrument !== 'all') {
        return column.id !== 'entityName';
      }
      return true;
    }
  );

  useEffect(() => {
    if (items) {
      const newModifiedItems = convertDataFromApi(
        items,
        tableKey,
        filter,
        {},
        additionalLoadsResults
      );

      setModifiedAlerts(newModifiedItems);
    }
  }, [items]);

  const reloadAlertList = hasFilter => {
    if (!hasFilter) {
      clearFilter();
    } else {
      reloadData({ delay: NULL_QUERY_DELAY });
    }
  };

  const redirectToAssessmentReport = ({ accountUUID, entityUUID, patientUUID, entityName }) => {
    const isItChildAccount = entityUUID !== currentAccount.uuid && isDistributionSponsor;
    dispatch(
      navigateToRoute({
        route: {
          path: (isItChildAccount ? ALL_ROUTES.assessment.reportDS : ALL_ROUTES.assessment.report)
            .path,
          params: {
            currentAccountUuid: currentAccount.uuid,
            accountUuid: isItChildAccount ? accountUUID : null,
            patientUuid: patientUUID,
            assessmentUuid: entityUUID,
            status: camelCase(ASSESSMENT_STATUSES.completed),
          },
          title: `${entityName} ${titles.report}`,
        },
      })
    );
  };

  const _onFilter = (_filter, delay) => {
    onFilter(_filter, delay);
    dispatch(setAlertsPreFilter(_filter(filter)));
  };

  const hasFilters = useMemo(() => {
    const baseFilterIds = baseFilter.map(filter => filter.id);
    return !!filter?.reduce((accumulator, { value, id }) => {
      if (baseFilterIds.includes(id)) {
        return accumulator;
      }
      return accumulator + value.toString().replaceAll(' ', '').length;
    }, 0);
  }, [filter]);

  return (
    <QhsTable
      columns={columns}
      data={modifiedAlerts || []}
      fetchNextPage={onLoadMoreRows}
      isFetching={dataLoading}
      isLoading={dataLoading && !modifiedAlerts}
      totalRowCount={totalCount}
      sorting={sort}
      setSorting={onSort}
      filter={filter}
      setFilters={_onFilter}
      tableKey={tableKey}
      onRowClick={redirectToAssessmentReport}
      showRefreshTableButton
      reloadData={reloadAlertList}
      fetchError={fetchError}
      tableTitle={
        <TableTitle titleText={titles.alerts} count={totalCount || 0} countFiltered={hasFilters} />
      }
      renderRowActionMenuItems={({ row, setAnchorEl }) => (
        <RowActions row={row} setAnchorEl={setAnchorEl} reloadData={reloadData} />
      )}
    />
  );
};

export default PatientQhsTable;
